import { useEffect, useRef } from 'react';
import { ArrowRight, Globe, CheckCircle } from 'lucide-react';
import whatsappLogo from '../assets/whatsapp-logo.svg';
import gsap from 'gsap';

const CALENDAR_URL = 'https://calendar.app.google/DtBNRYcoX9a3oD5a9';
const WHATSAPP_URL =
	'https://wa.me/919351275556?text=Hi%2C%20I%27d%20like%20to%20enquire%20about%20your%20India-UK%20tax%20advisory%20services.';

const STATS = [
	{ value: '1200+', label: 'Clients Served' },
	{ value: '8+', label: 'Years Experience' },
	{ value: '2', label: 'Jurisdictions' },
];

const BENEFITS = [
	'Zero double taxation with DTAA expertise',
	'CA India & ICAEW certified professionals',
	'Seamless India-UK financial management',
	'QROPS pension transfer specialists',
];

const Hero = () => {
	const heroRef = useRef(null);

	useEffect(() => {
		const ctx = gsap.context(() => {
			const tl = gsap.timeline({ delay: 0.2 });
			tl.from('.hero-badge', {
				opacity: 0,
				y: -15,
				duration: 0.6,
				ease: 'power2.out',
			})
				.from(
					'.hero-title',
					{ opacity: 0, y: 35, duration: 0.9, ease: 'power3.out' },
					'-=0.3',
				)
				.from(
					'.hero-subtitle',
					{ opacity: 0, y: 20, duration: 0.8, ease: 'power3.out' },
					'-=0.5',
				)
				.from(
					'.hero-cta-group',
					{ opacity: 0, y: 20, duration: 0.8, ease: 'power3.out' },
					'-=0.4',
				)
				.from(
					'.hero-benefits',
					{ opacity: 0, y: 15, duration: 0.7, ease: 'power2.out' },
					'-=0.4',
				)
				.from(
					'.hero-stats',
					{ opacity: 0, y: 15, duration: 0.7, ease: 'power2.out' },
					'-=0.3',
				)
				.from(
					'.hero-visual',
					{ opacity: 0, x: 50, duration: 1.0, ease: 'power3.out' },
					'-=1.1',
				);

			gsap.to('.float-1', {
				y: -12,
				duration: 3.2,
				repeat: -1,
				yoyo: true,
				ease: 'sine.inOut',
			});
			gsap.to('.float-2', {
				y: 14,
				duration: 3.8,
				repeat: -1,
				yoyo: true,
				ease: 'sine.inOut',
				delay: 0.7,
			});
			gsap.to('.float-3', {
				y: -8,
				duration: 2.8,
				repeat: -1,
				yoyo: true,
				ease: 'sine.inOut',
				delay: 1.2,
			});
		}, heroRef);
		return () => ctx.revert();
	}, []);

	return (
		<section
			ref={heroRef}
			data-testid='hero-section'
			id='home'
			aria-label='India-UK cross-border tax specialists'
			className='min-h-screen flex items-center relative overflow-hidden'
			style={{
				background:
					'linear-gradient(150deg, #FFFFFF 0%, #FFFBF0 55%, #FEF9EC 100%)',
				paddingTop: '72px',
			}}>
			{/* Background blobs */}
			<div
				className='absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none'
				style={{
					background:
						'radial-gradient(circle, rgba(245,158,11,0.18) 0%, transparent 70%)',
					transform: 'translate(30%, -30%)',
				}}
			/>
			<div
				className='absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full pointer-events-none'
				style={{
					background:
						'radial-gradient(circle, rgba(27,138,150,0.07) 0%, transparent 70%)',
					transform: 'translate(-30%, 30%)',
				}}
			/>
			<div
				className='absolute top-1/3 left-1/4 w-[340px] h-[340px] rounded-full pointer-events-none'
				style={{
					background:
						'radial-gradient(circle, rgba(245,158,11,0.10) 0%, transparent 70%)',
				}}
			/>

			<div className='max-w-6xl mx-auto px-6 w-full py-16'>
				<div className='grid md:grid-cols-2 gap-12 lg:gap-20 items-center'>
					{/* Left — Content */}
					<div className='space-y-7'>
						{/* Badge */}
						<div
							className='hero-badge inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold'
							style={{
								backgroundColor: '#FEF3C7',
								color: '#92400E',
								border: '1px solid #FCD34D',
							}}>
							<span
								className='w-1.5 h-1.5 rounded-full animate-pulse'
								style={{ backgroundColor: '#D97706' }}
							/>
							India-UK Cross-Border Tax Specialists
						</div>

						{/* Headline */}
						<h1
							className='hero-title text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] tracking-tight'
							style={{ color: '#1F2937', fontFamily: "'Outfit', sans-serif" }}>
							Navigate Global{' '}
							<span className='relative inline-block'>
								<span
									style={{
										color: '#2D5A8C',
										background: 'linear-gradient(90deg, #2D5A8C, #1B8A96)',
										WebkitBackgroundClip: 'text',
										WebkitTextFillColor: 'transparent',
									}}>
									Taxation
								</span>
								<span
									aria-hidden='true'
									className='absolute left-0 right-0'
									style={{
										bottom: '0.05em',
										height: '0.18em',
										background:
											'linear-gradient(90deg, rgba(245,158,11,0.55) 0%, rgba(245,158,11,0.25) 100%)',
										borderRadius: '2px',
										zIndex: -1,
									}}
								/>
							</span>
							<br />
							with Confidence
						</h1>

						{/* Subtitle */}
						<p
							className='hero-subtitle text-base sm:text-lg leading-relaxed max-w-lg'
							style={{ color: '#6B7280' }}>
							Expert guidance for India-UK cross-border taxation, wealth
							optimisation, and strategic business setup. Eliminate double
							taxation using DTAA treaty benefits.
						</p>

						{/* CTAs */}
						<div className='hero-cta-group flex flex-wrap gap-3'>
							<a
								data-testid='hero-primary-cta'
								href={CALENDAR_URL}
								target='_blank'
								rel='noopener noreferrer'
								className='inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-95'
								style={{
									background:
										'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
									boxShadow: '0 4px 20px rgba(245,158,11,0.35)',
								}}>
								Schedule Your Consultation
								<ArrowRight size={15} />
							</a>
							<a
								data-testid='hero-whatsapp-cta'
								href={WHATSAPP_URL}
								target='_blank'
								rel='noopener noreferrer'
								className='inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 hover:-translate-y-0.5 active:scale-95'
								style={{
									backgroundColor: '#25D366',
									boxShadow: '0 4px 16px rgba(37,211,102,0.30)',
								}}>
								<img src={whatsappLogo} alt='' className='w-6 h-6 -mx-1' />
								Chat on WhatsApp
							</a>
							<button
								data-testid='hero-secondary-cta'
								onClick={() =>
									document
										.getElementById('services')
										?.scrollIntoView({ behavior: 'smooth' })
								}
								className='inline-flex items-center gap-2 px-6 py-3.5 rounded-xl text-sm font-semibold border-2 transition-all duration-200 hover:bg-blue-50'
								style={{ color: '#2D5A8C', borderColor: '#2D5A8C' }}>
								Explore Services
							</button>
						</div>

						{/* Benefits */}
						<div className='hero-benefits flex flex-col gap-2.5'>
							{BENEFITS.map((item) => (
								<div key={item} className='flex items-center gap-2.5'>
									<CheckCircle
										size={15}
										style={{ color: '#2D5A8C', flexShrink: 0 }}
									/>
									<span className='text-sm' style={{ color: '#4B5563' }}>
										{item}
									</span>
								</div>
							))}
						</div>

						{/* Stats strip — visible on all screens */}
						<div
							className='hero-stats flex gap-6 pt-2 border-t'
							style={{ borderColor: 'rgba(45,90,140,0.12)' }}>
							{STATS.map(({ value, label }) => (
								<div key={label}>
									<div
										className='text-2xl font-bold'
										style={{
											color: '#2D5A8C',
											fontFamily: "'Outfit', sans-serif",
										}}>
										{value}
									</div>
									<div className='text-xs' style={{ color: '#9CA3AF' }}>
										{label}
									</div>
								</div>
							))}
						</div>
					</div>

					{/* Right — Visual (desktop only) */}
					<div
						className='hero-visual relative hidden md:flex justify-center items-center'
						style={{ height: '460px' }}>
						{/* Outer ring */}
						<div
							className='float-1 absolute w-96 h-96 rounded-full flex items-center justify-center'
							style={{
								background:
									'linear-gradient(135deg, rgba(45,90,140,0.07) 0%, rgba(27,138,150,0.10) 100%)',
								border: '1px solid rgba(45,90,140,0.12)',
							}}>
							{/* Middle ring */}
							<div
								className='w-72 h-72 rounded-full flex items-center justify-center'
								style={{
									background:
										'linear-gradient(135deg, rgba(45,90,140,0.11) 0%, rgba(27,138,150,0.17) 100%)',
									border: '1px solid rgba(45,90,140,0.16)',
								}}>
								{/* Globe core */}
								<div
									className='w-40 h-40 rounded-full flex items-center justify-center shadow-2xl'
									style={{
										background:
											'linear-gradient(135deg, #2D5A8C 0%, #1B8A96 100%)',
									}}>
									<Globe size={68} color='white' strokeWidth={1.2} />
								</div>
							</div>
						</div>

						{/* Floating card — DTAA */}
						<div
							className='float-2 absolute top-8 left-0 bg-white rounded-2xl shadow-lg px-4 py-3'
							style={{ border: '1px solid #F3F4F6', minWidth: '168px' }}>
							<div className='flex items-center gap-2 mb-1'>
								<span className='text-base'>🛡️</span>
								<span
									className='text-xs font-medium'
									style={{ color: '#6B7280' }}>
									DTAA Protected
								</span>
							</div>
							<div className='font-bold text-sm' style={{ color: '#1F2937' }}>
								Zero Double Taxation
							</div>
						</div>

						{/* Floating card — Clients */}
						<div
							className='float-3 absolute bottom-16 right-0 bg-white rounded-2xl shadow-lg px-4 py-3'
							style={{ border: '1px solid #F3F4F6', minWidth: '148px' }}>
							<div
								className='text-xs font-medium mb-1'
								style={{ color: '#6B7280' }}>
								Clients Served
							</div>
							<div className='font-bold text-2xl' style={{ color: '#2D5A8C' }}>
								1200+
							</div>
						</div>

						{/* Jurisdiction pill */}
						<div
							className='float-2 absolute bottom-6 left-4 bg-white rounded-xl shadow-md px-4 py-2.5 flex items-center gap-2'
							style={{ border: '1px solid #F3F4F6' }}>
							<span
								className='px-2 py-0.5 rounded text-xs font-bold'
								style={{ backgroundColor: '#FFF0E0', color: '#D97706' }}>
								IN
							</span>
							<span className='text-xs' style={{ color: '#9CA3AF' }}>
								+
							</span>
							<span
								className='px-2 py-0.5 rounded text-xs font-bold'
								style={{ backgroundColor: '#EEF4FF', color: '#2D5A8C' }}>
								UK
							</span>
							<span
								className='text-xs font-semibold'
								style={{ color: '#1F2937' }}>
								Dual Jurisdiction
							</span>
						</div>

						{/* QROPS pill */}
						<div
							className='float-3 absolute top-12 right-0 bg-white rounded-xl shadow-md px-4 py-2.5 flex items-center gap-2'
							style={{ border: '1px solid #F3F4F6' }}>
							<Globe size={12} style={{ color: '#1B8A96' }} />
							<span
								className='text-xs font-semibold'
								style={{ color: '#1F2937' }}>
								QROPS Pension Transfer
							</span>
						</div>
					</div>
				</div>
			</div>

			{/* Scroll indicator */}
			<div className='absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1.5 opacity-40'>
				<span className='text-xs' style={{ color: '#6B7280' }}>
					Scroll to explore
				</span>
				<div
					className='w-px h-8 rounded-full'
					style={{ backgroundColor: '#9CA3AF' }}
				/>
			</div>
		</section>
	);
};

export default Hero;
